import type { EventHandler, VFC } from 'react'
import React, { useCallback, useMemo, useState } from 'react'
import ActionsDropdown from './ActionsDropdown'
import { Body, Button, Input, Title, useModal, CopyButton, LoadingIcon } from '@panwds/react-ui'
import {
  BUTTON_ADD_OWNER, BUTTON_DOWNLOAD_SP,
  BUTTON_EDIT_FED_MAP, LABEL_ASSERTION_URL,
  LABEL_DISABLED,
  LABEL_ENABLED, LABEL_ENTITY_ID, LABEL_UPDATING
} from '../../../../constants/LabelConstants'
import { IDP_TABS } from './index'
import { CircleCheckIcon, MinusCircleIcon, CaretUpIcon, DownloadIcon } from '@panwds/icons'
import Collapse from '../../../../../common/components/collapse/Collapse'
import AddOwnersModal from '../../modal/AddOwnersModal'
import TenantMappingModal from '../../modal/TenantMappingModal'
import { getVarValue } from '../../../../../common/utils/Sparky'
import { FF_ENABLE_FEDERATION_MAPPING } from '../../../../../common/utils/FeatureFlags'
import type { IAM_IDP } from '../../../../../common/reducers/types/IAMReducer.types'

// Feature Flags
const enableFederationMapping = getVarValue(FF_ENABLE_FEDERATION_MAPPING)

const DetailsToggle: VFC<{
  isOpen: boolean,
  onClick: EventHandler<any>
}> = ({ isOpen, onClick }) => {
  return (
    <button
      aria-label={isOpen ? 'Collapse Idp' : 'Expand Idp'}
      type='button'
      className='tw-border-none tw-p-0 tw-m-0 focus:tw-outline-none tw-bg-transparent'
      onClick={onClick}
    >
      <CaretUpIcon
        size='sm'
        className={`tw-transition-transform tw-duration-200 tw-transform ${
          isOpen ? 'tw-rotate-180' : 'tw-rotate-0'
        }`}
      />
    </button>
  )
}

const ProviderCardHeader: VFC<{
  activeTabId: string,
  actions: any[],
  isLoading: boolean,
  domain: string,
  idp?: IAM_IDP,
  fetchData: Function,
  isStatusLoading?: boolean,
  onSPMetaClick?: any,
  isVerified?: boolean
}> = ({
  activeTabId,
  actions,
  isLoading,
  domain,
  idp,
  fetchData,
  isStatusLoading,
  onSPMetaClick,
  isVerified
}) => {

  // State
  const [isOpen, setIsOpen] = useState(false)

  // Modals
  const addOwnerModal: { openModal: Function } = useModal(AddOwnersModal)
  const tenantMappingModal: { openModal: Function } = useModal(TenantMappingModal)

  const RightControls = useMemo(() => {
    return (
      <div className='tw-flex tw-gap-2 tw-items-center'>
        {!enableFederationMapping && isVerified && <Button appearance='secondary' disabled={isLoading} onClick={onSPMetaClick} icon={<DownloadIcon />}>{BUTTON_DOWNLOAD_SP}</Button>}
        {isLoading && <LoadingIcon size='xs'/>}
        {isVerified && activeTabId === IDP_TABS.OWNERS.id && (
          <Button
            appearance='secondary'
            onClick={() => addOwnerModal.openModal({
              domain,
              fetchData
            })}
          >{BUTTON_ADD_OWNER}</Button>
        )}
        {!!enableFederationMapping && (
          <>
            {activeTabId === IDP_TABS.MAPPING.id && (
              <Button
                appearance='secondary'
                onClick={() => tenantMappingModal.openModal({
                  domain,
                  fetchData
                })}
              >{BUTTON_EDIT_FED_MAP}</Button>
            )}
          </>
        )}
        <ActionsDropdown
          idp={idp}
          isLoading={isLoading || isStatusLoading}
          {...{ actions }}/>
      </div>
    )
  }, [isVerified, isLoading, idp, onSPMetaClick, isStatusLoading, actions, activeTabId, addOwnerModal, domain, fetchData, tenantMappingModal])

  const idpStatus = useMemo(() => {
    if (isStatusLoading) {
      return LABEL_UPDATING
    }
    return idp?.isActive ? LABEL_ENABLED : LABEL_DISABLED
  }, [idp?.isActive, isStatusLoading])

  const icon = useMemo(() => {
    if (isStatusLoading) {
      return <LoadingIcon size='sm'/>
    }
    return idp?.isActive ? (
      <CircleCheckIcon className='tw-text-green-400 dark:tw-text-dark-bg-green'/>
    ) : (
      <MinusCircleIcon className='tw-text-red-400 dark:tw-text-dark-bg-red' />
    )
  }, [idp?.isActive, isStatusLoading])

  const onDetailsClick = useCallback(() => {
    setIsOpen(!isOpen)
  }, [isOpen])

  return (
    <>
      <div className='tw-flex tw-justify-between tw-p-4 tw-border tw-border-solid tw-border-gray-200 tw-border-r-0 tw-border-t-0 tw-border-l-0'>
        <div className='tw-flex tw-gap-2 tw-items-baseline'>
          <Title level={2} size='sm' truncate>{domain}</Title>
          {idp && (
            <div>
              <div className='tw-flex tw-gap-2 tw-items-center'>
                <DetailsToggle isOpen={isOpen} onClick={onDetailsClick}/>
                {icon}
                <Body>{idpStatus}</Body>
              </div>
            </div>
          )}
        </div>
        {RightControls}
      </div>
      <Collapse isOpen={isOpen}>

        <div className='tw-flex tw-p-3 tw-gap-2'>

          <div className='tw-flex tw-flex-col tw-gap-2'>
            <div className='tw-flex tw-items-center tw-flex-1'>
              <Body>{LABEL_ENTITY_ID}</Body>
            </div>
            <div className='tw-flex tw-items-center tw-flex-1'>
              <Body>{LABEL_ASSERTION_URL}</Body>
            </div>
          </div>

          <div className='tw-flex tw-flex-col tw-gap-2 tw-flex-1'>
            <div className='tw-flex-1'>
              <Input readOnly value={idp?.config?.entityId} />
            </div>
            <div className='tw-flex-1'>
              <Input readOnly value={idp?.config?.ssoServiceUrl}/>
            </div>
          </div>

          <div className='tw-flex tw-flex-col tw-gap-2'>
            <div className='tw-flex-1'>
              <CopyButton
                appearance='secondary'
                text={() => (idp?.config?.entityId as string)}
              />
            </div>
            <div className='tw-flex-1'>
              <CopyButton
                appearance='secondary'
                text={() => (idp?.config?.ssoServiceUrl as string)}
              />
            </div>
          </div>

        </div>

      </Collapse>
    </>
  )
}

export default ProviderCardHeader
